import { z } from 'zod';
import { FlowBlockValueFieldSchemaV2, FlowBlockValueFieldSchemaVariant, } from '../blockValue/fieldSchema.js';
import { FlowBlockValueNumberSchema } from '../blockValue/numberSchema.js';
import { FlowBlockValueStringSchema } from '../blockValue/stringSchema.js';
export var FlowConditionalOperation;
(function (FlowConditionalOperation) {
    FlowConditionalOperation["Equals"] = "Equals";
    FlowConditionalOperation["NotEquals"] = "NotEquals";
    FlowConditionalOperation["InRange"] = "InRange";
    FlowConditionalOperation["NotInRange"] = "NotInRange";
    FlowConditionalOperation["Contains"] = "Contains";
    FlowConditionalOperation["DoesNotContain"] = "DoesNotContain";
    FlowConditionalOperation["StartsWith"] = "StartsWith";
    FlowConditionalOperation["DoesNotStartWith"] = "DoesNotStartWith";
    FlowConditionalOperation["EndsWith"] = "EndsWith";
    FlowConditionalOperation["DoesNotEndWith"] = "DoesNotEndWith";
    FlowConditionalOperation["IsOneOf"] = "IsOneOf";
    FlowConditionalOperation["IsNotOneOf"] = "IsNotOneOf";
    FlowConditionalOperation["GreaterThan"] = "GreaterThan";
    FlowConditionalOperation["GreaterThanOrEqualTo"] = "GreaterThanOrEqualTo";
    FlowConditionalOperation["LessThan"] = "LessThan";
    FlowConditionalOperation["LessThanOrEqualTo"] = "LessThanOrEqualTo";
    FlowConditionalOperation["IsTrue"] = "IsTrue";
    FlowConditionalOperation["IsFalse"] = "IsFalse";
    FlowConditionalOperation["Exists"] = "Exists";
    FlowConditionalOperation["DoesNotExist"] = "DoesNotExist";
})(FlowConditionalOperation || (FlowConditionalOperation = {}));
export var FlowConditionalFieldDataType;
(function (FlowConditionalFieldDataType) {
    FlowConditionalFieldDataType["String"] = "String";
    FlowConditionalFieldDataType["Number"] = "Number";
    FlowConditionalFieldDataType["Date"] = "Date";
    FlowConditionalFieldDataType["DateTime"] = "DateTime";
    FlowConditionalFieldDataType["Boolean"] = "Boolean";
    FlowConditionalFieldDataType["Value"] = "Value";
})(FlowConditionalFieldDataType || (FlowConditionalFieldDataType = {}));
export var FlowConditionalCaseSensitivity;
(function (FlowConditionalCaseSensitivity) {
    FlowConditionalCaseSensitivity["ExactMatch"] = "ExactMatch";
    FlowConditionalCaseSensitivity["IgnoreCase"] = "IgnoreCase";
})(FlowConditionalCaseSensitivity || (FlowConditionalCaseSensitivity = {}));
export var FlowConditionalType;
(function (FlowConditionalType) {
    FlowConditionalType["MatchFirst"] = "MatchFirst";
    FlowConditionalType["MatchAll"] = "MatchAll";
})(FlowConditionalType || (FlowConditionalType = {}));
export const FLOW_CONDITIONAL_ELSE_CONDITION_ID = '__Else__';
/** Permissive UUID check matching pre-Zod-4 `z.uuid()`, for legacy stored condition ids. */
const conditionIdSchema = z
    .string()
    .regex(/^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/i, {
    message: 'Invalid UUID',
});
const stringWithValueBaseConditionSchema = z.object({
    id: conditionIdSchema,
    label: z.string().optional(),
    inputValue: FlowBlockValueFieldSchemaV2({
        variant: FlowBlockValueFieldSchemaVariant.TemplateOnly,
    }),
    dataType: z.literal(FlowConditionalFieldDataType.String),
    value: FlowBlockValueStringSchema({
        stringValidationSchema: z.string(),
    }),
    caseSensitivity: z
        .enum(FlowConditionalCaseSensitivity)
        .default(FlowConditionalCaseSensitivity.ExactMatch),
});
export const FLOW_CONDITIONAL_STRING_CONDITION_SCHEMAS = [
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.Equals),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.NotEquals),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.Contains),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.DoesNotContain),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.IsOneOf),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.IsNotOneOf),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.StartsWith),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.DoesNotStartWith),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.EndsWith),
    }),
    stringWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.DoesNotEndWith),
    }),
];
export const flowConditionalStringConditionSchema = z.union(FLOW_CONDITIONAL_STRING_CONDITION_SCHEMAS);
const numberWithValueBaseConditionSchema = z.object({
    id: conditionIdSchema,
    label: z.string().optional(),
    inputValue: FlowBlockValueFieldSchemaV2({
        variant: FlowBlockValueFieldSchemaVariant.TemplateOnly,
    }),
    dataType: z.literal(FlowConditionalFieldDataType.Number),
    value: FlowBlockValueNumberSchema({ numberValidationSchema: z.number() }),
});
const numberWithRangeBaseConditionSchema = z.object({
    id: conditionIdSchema,
    label: z.string().optional(),
    inputValue: FlowBlockValueFieldSchemaV2({
        variant: FlowBlockValueFieldSchemaVariant.TemplateOnly,
    }),
    dataType: z.literal(FlowConditionalFieldDataType.Number),
    minValue: FlowBlockValueNumberSchema({ numberValidationSchema: z.number() }),
    maxValue: FlowBlockValueNumberSchema({ numberValidationSchema: z.number() }),
    minValueInclusive: z.boolean(),
    maxValueInclusive: z.boolean(),
});
export const FLOW_CONDITIONAL_NUMBER_CONDITION_SCHEMAS = [
    numberWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.Equals),
    }),
    numberWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.NotEquals),
    }),
    numberWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.GreaterThan),
    }),
    numberWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.GreaterThanOrEqualTo),
    }),
    numberWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.LessThan),
    }),
    numberWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.LessThanOrEqualTo),
    }),
    numberWithRangeBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.InRange),
    }),
    numberWithRangeBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.NotInRange),
    }),
];
export const flowConditionalNumberConditionSchema = z.union(FLOW_CONDITIONAL_NUMBER_CONDITION_SCHEMAS);
const dateWithValueBaseConditionSchema = z.object({
    id: conditionIdSchema,
    label: z.string().optional(),
    inputValue: FlowBlockValueFieldSchemaV2({
        variant: FlowBlockValueFieldSchemaVariant.TemplateOnly,
    }),
    dataType: z.literal(FlowConditionalFieldDataType.Date),
    value: z.union([
        FlowBlockValueFieldSchemaV2({ variant: FlowBlockValueFieldSchemaVariant.TemplateOnly }),
        z.iso.datetime(),
        z.iso.date(),
    ]),
});
export const FLOW_CONDITIONAL_DATE_CONDITION_SCHEMAS = [
    dateWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.Equals),
    }),
    dateWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.NotEquals),
    }),
    dateWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.GreaterThan),
    }),
    dateWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.GreaterThanOrEqualTo),
    }),
    dateWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.LessThan),
    }),
    dateWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.LessThanOrEqualTo),
    }),
];
export const flowConditionalDateConditionSchema = z.union(FLOW_CONDITIONAL_DATE_CONDITION_SCHEMAS);
const dateTimeWithValueBaseConditionSchema = z.object({
    id: conditionIdSchema,
    label: z.string().optional(),
    inputValue: FlowBlockValueFieldSchemaV2({
        variant: FlowBlockValueFieldSchemaVariant.TemplateOnly,
    }),
    dataType: z.literal(FlowConditionalFieldDataType.DateTime),
    value: z.union([
        FlowBlockValueFieldSchemaV2({ variant: FlowBlockValueFieldSchemaVariant.TemplateOnly }),
        z.iso.datetime(),
    ]),
});
export const FLOW_CONDITIONAL_DATE_TIME_CONDITION_SCHEMAS = [
    dateTimeWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.Equals),
    }),
    dateTimeWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.NotEquals),
    }),
    dateTimeWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.GreaterThan),
    }),
    dateTimeWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.GreaterThanOrEqualTo),
    }),
    dateTimeWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.LessThan),
    }),
    dateTimeWithValueBaseConditionSchema.extend({
        operation: z.literal(FlowConditionalOperation.LessThanOrEqualTo),
    }),
];
export const flowConditionalDateTimeConditionSchema = z.union(FLOW_CONDITIONAL_DATE_TIME_CONDITION_SCHEMAS);
const booleanConditionBaseSchema = z.object({
    id: conditionIdSchema,
    label: z.string().optional(),
    inputValue: FlowBlockValueFieldSchemaV2({
        variant: FlowBlockValueFieldSchemaVariant.TemplateOnly,
    }),
    dataType: z.literal(FlowConditionalFieldDataType.Boolean),
});
export const FLOW_CONDITIONAL_BOOLEAN_CONDITION_SCHEMAS = [
    booleanConditionBaseSchema.extend({
        operation: z.literal(FlowConditionalOperation.IsTrue),
    }),
    booleanConditionBaseSchema.extend({
        operation: z.literal(FlowConditionalOperation.IsFalse),
    }),
];
export const flowConditionalBooleanConditionSchema = z.union(FLOW_CONDITIONAL_BOOLEAN_CONDITION_SCHEMAS);
const valueConditionBaseSchema = z.object({
    id: conditionIdSchema,
    label: z.string().optional(),
    inputValue: FlowBlockValueFieldSchemaV2({
        variant: FlowBlockValueFieldSchemaVariant.TemplateOnly,
    }),
    dataType: z.literal(FlowConditionalFieldDataType.Value),
});
export const FLOW_CONDITIONAL_VALUE_CONDITION_SCHEMAS = [
    valueConditionBaseSchema.extend({
        operation: z.literal(FlowConditionalOperation.Exists),
    }),
    valueConditionBaseSchema.extend({
        operation: z.literal(FlowConditionalOperation.DoesNotExist),
    }),
];
export const flowConditionalValueConditionSchema = z.union(FLOW_CONDITIONAL_VALUE_CONDITION_SCHEMAS);
export const FLOW_ALL_CONDITION_SCHEMAS = [
    ...FLOW_CONDITIONAL_STRING_CONDITION_SCHEMAS,
    ...FLOW_CONDITIONAL_NUMBER_CONDITION_SCHEMAS,
    ...FLOW_CONDITIONAL_DATE_CONDITION_SCHEMAS,
    ...FLOW_CONDITIONAL_DATE_TIME_CONDITION_SCHEMAS,
    ...FLOW_CONDITIONAL_BOOLEAN_CONDITION_SCHEMAS,
    ...FLOW_CONDITIONAL_VALUE_CONDITION_SCHEMAS,
];
const _ConditionSchema = z.union(FLOW_ALL_CONDITION_SCHEMAS);
const baseConditionSchema = z
    .object({
    id: conditionIdSchema,
    label: z.string().optional(),
    inputValue: FlowBlockValueFieldSchemaV2({
        variant: FlowBlockValueFieldSchemaVariant.TemplateOnly,
    }),
    dataType: z.enum(FlowConditionalFieldDataType),
    operation: z.enum(FlowConditionalOperation),
})
    .loose();
export const flowConditionalConditionSchema = baseConditionSchema.superRefine((arg, context) => {
    for (const schema of FLOW_ALL_CONDITION_SCHEMAS) {
        if (schema.shape.dataType.value !== arg.dataType) {
            continue;
        }
        if (schema.shape.operation.value !== arg.operation) {
            continue;
        }
        const parseResult = schema.safeParse(arg);
        if (parseResult.success) {
            return z.NEVER;
        }
        parseResult.error.issues.forEach((issue) => {
            context.addIssue(issue);
        });
        return z.NEVER;
    }
    context.addIssue({
        code: 'custom',
        message: 'Invalid condition',
    });
    return z.NEVER;
});
export const flowConditionalConfigSchema = z.object({
    conditionalType: z.enum(FlowConditionalType),
    conditions: z.array(flowConditionalConditionSchema),
});
//# sourceMappingURL=conditionalConfig.js.map